import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import Loading from "@/components/Common/Loading";
import Page from "@/components/Common/Page";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import { callApi, default as query } from "@/Utils/request/query";

import {
  MVPBillingConfigurationWrite,
  MVPPriceListWrite,
} from "@/types/mvp/billing";
import mvpBillingApi from "@/types/mvp/billingApi";

const defaultConfiguration: MVPBillingConfigurationWrite = {
  currency: "INR",
  default_tax_rate: "0",
  rounding_scale: 2,
  tax_inclusive: false,
  require_estimate_approval: true,
  metadata: {},
};

export function MVPBillingCatalog() {
  const { t } = useTranslation();
  const { facilityId } = useCurrentFacility();
  const queryClient = useQueryClient();
  const [configurationForm, setConfigurationForm] =
    useState<MVPBillingConfigurationWrite>(defaultConfiguration);
  const [priceListForm, setPriceListForm] = useState<MVPPriceListWrite>({
    code: "",
    name: "",
    currency: "INR",
    effective_from: new Date().toISOString(),
    priority: 0,
  });

  const configurationQuery = useQuery({
    queryKey: ["mvp-billing-configuration", facilityId],
    queryFn: query(mvpBillingApi.listConfigurations, {
      pathParams: { facilityId },
    }),
  });
  const priceListsQuery = useQuery({
    queryKey: ["mvp-billing-price-lists", facilityId],
    queryFn: query(mvpBillingApi.listPriceLists, {
      pathParams: { facilityId },
    }),
  });

  const configuration = configurationQuery.data?.results[0];
  useEffect(() => {
    if (!configuration) return;
    setConfigurationForm({
      currency: configuration.currency,
      default_tax_rate: configuration.default_tax_rate,
      rounding_scale: configuration.rounding_scale,
      tax_inclusive: configuration.tax_inclusive,
      require_estimate_approval: configuration.require_estimate_approval,
      metadata: configuration.metadata,
    });
  }, [configuration]);

  const saveConfiguration = useMutation({
    mutationFn: (body: MVPBillingConfigurationWrite) =>
      configuration
        ? callApi(mvpBillingApi.updateConfiguration, {
            pathParams: { facilityId, configurationId: configuration.id },
            body,
          })
        : callApi(mvpBillingApi.createConfiguration, {
            pathParams: { facilityId },
            body,
          }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["mvp-billing-configuration", facilityId],
      });
    },
  });

  const createPriceList = useMutation({
    mutationFn: (body: MVPPriceListWrite) =>
      callApi(mvpBillingApi.createPriceList, {
        pathParams: { facilityId },
        body,
      }),
    onSuccess: () => {
      setPriceListForm((current) => ({ ...current, code: "", name: "" }));
      queryClient.invalidateQueries({
        queryKey: ["mvp-billing-price-lists", facilityId],
      });
    },
  });

  if (configurationQuery.isLoading || priceListsQuery.isLoading) {
    return <Loading />;
  }

  return (
    <Page
      title={t("mvp_billing_catalog", {
        defaultValue: "MVP Billing Catalog",
      })}
    >
      <div className="mx-auto mt-8 grid w-full max-w-5xl gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              {t("mvp_billing_configuration", {
                defaultValue: "Billing configuration",
              })}
            </CardTitle>
            <CardDescription>
              {t("mvp_billing_configuration_description", {
                defaultValue:
                  "Set the facility currency, default tax, rounding precision, and estimate approval policy.",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4 md:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                saveConfiguration.mutate(configurationForm);
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="mvp-currency">
                  {t("currency", { defaultValue: "Currency" })}
                </Label>
                <Input
                  id="mvp-currency"
                  maxLength={3}
                  value={configurationForm.currency}
                  onChange={(event) =>
                    setConfigurationForm((current) => ({
                      ...current,
                      currency: event.target.value.toUpperCase(),
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="mvp-tax-rate">
                  {t("mvp_default_tax_rate", {
                    defaultValue: "Default tax rate (%)",
                  })}
                </Label>
                <Input
                  id="mvp-tax-rate"
                  min={0}
                  max={100}
                  step="0.0001"
                  type="number"
                  value={configurationForm.default_tax_rate}
                  onChange={(event) =>
                    setConfigurationForm((current) => ({
                      ...current,
                      default_tax_rate: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="mvp-rounding-scale">
                  {t("mvp_rounding_decimals", {
                    defaultValue: "Rounding decimals",
                  })}
                </Label>
                <Input
                  id="mvp-rounding-scale"
                  min={0}
                  max={6}
                  type="number"
                  value={configurationForm.rounding_scale}
                  onChange={(event) =>
                    setConfigurationForm((current) => ({
                      ...current,
                      rounding_scale: Number(event.target.value),
                    }))
                  }
                />
              </div>
              <label className="flex items-center gap-2 self-end text-sm">
                <input
                  type="checkbox"
                  checked={configurationForm.tax_inclusive}
                  onChange={(event) =>
                    setConfigurationForm((current) => ({
                      ...current,
                      tax_inclusive: event.target.checked,
                    }))
                  }
                />
                {t("mvp_prices_include_tax", {
                  defaultValue: "Prices include tax",
                })}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={configurationForm.require_estimate_approval}
                  onChange={(event) =>
                    setConfigurationForm((current) => ({
                      ...current,
                      require_estimate_approval: event.target.checked,
                    }))
                  }
                />
                {t("mvp_require_estimate_approval", {
                  defaultValue: "Require estimate approval",
                })}
              </label>
              <div className="md:col-span-2">
                <Button type="submit" disabled={saveConfiguration.isPending}>
                  {saveConfiguration.isPending
                    ? t("saving", { defaultValue: "Saving…" })
                    : t("save_configuration", {
                        defaultValue: "Save configuration",
                      })}
                </Button>
              </div>
            </form>
            {saveConfiguration.isError && (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {t("mvp_billing_configuration_save_error", {
                  defaultValue:
                    "Configuration could not be saved. Check your billing permission and the validation message from the server.",
                })}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("mvp_price_lists", { defaultValue: "Price lists" })}
            </CardTitle>
            <CardDescription>
              {t("mvp_price_lists_description", {
                defaultValue:
                  "Create facility-owned price-list versions before adding tariffs.",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6">
            <form
              className="grid gap-4 md:grid-cols-[1fr_2fr_auto]"
              onSubmit={(event) => {
                event.preventDefault();
                createPriceList.mutate(priceListForm);
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="mvp-price-list-code">
                  {t("code", { defaultValue: "Code" })}
                </Label>
                <Input
                  id="mvp-price-list-code"
                  required
                  value={priceListForm.code}
                  onChange={(event) =>
                    setPriceListForm((current) => ({
                      ...current,
                      code: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="mvp-price-list-name">
                  {t("name", { defaultValue: "Name" })}
                </Label>
                <Input
                  id="mvp-price-list-name"
                  required
                  value={priceListForm.name}
                  onChange={(event) =>
                    setPriceListForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                />
              </div>
              <Button
                className="self-end"
                disabled={createPriceList.isPending}
                type="submit"
              >
                {createPriceList.isPending
                  ? t("adding", { defaultValue: "Adding…" })
                  : t("mvp_add_price_list", { defaultValue: "Add price list" })}
              </Button>
            </form>
            {createPriceList.isError && (
              <p className="text-sm text-red-600" role="alert">
                {t("mvp_price_list_add_error", {
                  defaultValue:
                    "Price list could not be added. Codes must be unique per facility.",
                })}
              </p>
            )}
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 font-medium">
                      {t("code", { defaultValue: "Code" })}
                    </th>
                    <th className="px-4 py-3 font-medium">
                      {t("name", { defaultValue: "Name" })}
                    </th>
                    <th className="px-4 py-3 font-medium">
                      {t("currency", { defaultValue: "Currency" })}
                    </th>
                    <th className="px-4 py-3 font-medium">
                      {t("status", { defaultValue: "Status" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(priceListsQuery.data?.results ?? []).map((priceList) => (
                    <tr key={priceList.id} className="border-t">
                      <td className="px-4 py-3 font-mono">{priceList.code}</td>
                      <td className="px-4 py-3">{priceList.name}</td>
                      <td className="px-4 py-3">{priceList.currency}</td>
                      <td className="px-4 py-3">{priceList.status}</td>
                    </tr>
                  ))}
                  {!priceListsQuery.data?.results.length && (
                    <tr>
                      <td className="px-4 py-4 text-gray-500" colSpan={4}>
                        {t("mvp_no_price_lists", {
                          defaultValue:
                            "No price lists have been configured yet.",
                        })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}
